/**
 * WebSocket hook for real-time collaboration
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../config/api';

const BACKEND_URL = API_BASE_URL;
// WebSocket URL - convert HTTPS to WSS
const WS_URL = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

export function useCollaborationSocket(spaceId, user, onEvent) {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const previousUserRef = useRef(null);

  // Connect to WebSocket
  useEffect(() => {
    // Skip if no spaceId or user
    if (!spaceId || !user) {
      console.log('[WS] Skipping connection - missing spaceId or user', { spaceId, user });
      return;
    }

    // Skip if user hasn't changed (to prevent unnecessary reconnections)
    const userKey = `${user.user_id}_${user.name}`;
    if (previousUserRef.current === userKey && socketRef.current?.connected) {
      console.log('[WS] User unchanged, keeping existing connection');
      return;
    }
    previousUserRef.current = userKey;

    // Cleanup existing socket if any
    if (socketRef.current) {
      console.log('[WS] Cleaning up existing socket before new connection');
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    console.log('[WS] Creating new connection for:', { spaceId, userId: user.user_id, name: user.name, isGuest: user.is_guest });

    // Create socket connection
    // Path must match backend mount: /api/ws/socket.io
    const socket = io(WS_URL, {
      path: '/api/ws/socket.io/',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[WS] Connected to collaboration server, socket id:', socket.id);
      setConnected(true);

      // Join the space room
      socket.emit('join_space', {
        space_id: spaceId,
        user_id: user.user_id,
        email: user.email || null,  // Guests may not have email
        name: user.name || 'Guest',
        is_guest: user.is_guest || false,
        avatar_id: user.avatar_id || null,
        avatar: user.avatar || null, // Profile picture URL for authenticated users
      });
    });

    socket.on('disconnect', (reason) => {
      console.log('[WS] Disconnected from collaboration server, reason:', reason);
      setConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('[WS] Connection error:', error.message);
    });

    socket.on('error', (error) => {
      console.error('[WS] Socket error:', error);
    });

    // Handle presence updates
    socket.on('presence_update', (data) => {
      setActiveUsers(data.users || []);
      onEvent?.('presence_update', data);
    });

    socket.on('user_joined', (data) => {
      console.log('[WS] User joined:', data.name);
      onEvent?.('user_joined', data);
    });

    socket.on('user_left', (data) => {
      console.log('[WS] User left:', data.name);
      onEvent?.('user_left', data);
    });

    // Handle cursor updates
    socket.on('cursor_update', (data) => {
      onEvent?.('cursor_update', data);
    });

    // Handle element updates
    socket.on('element_created', (data) => {
      onEvent?.('element_created', data);
    });

    socket.on('element_updated', (data) => {
      onEvent?.('element_updated', data);
    });

    socket.on('element_deleted', (data) => {
      onEvent?.('element_deleted', data);
    });

    // Handle reaction updates
    socket.on('reaction_added', (data) => {
      onEvent?.('reaction_added', data);
    });

    socket.on('reaction_removed', (data) => {
      onEvent?.('reaction_removed', data);
    });

    // Handle facilitation events (voting, timer, etc.)
    socket.on('voting_started', (data) => {
      onEvent?.('voting_started', data);
    });

    socket.on('voting_ended', (data) => {
      onEvent?.('voting_ended', data);
    });

    socket.on('vote_cast', (data) => {
      onEvent?.('vote_cast', data);
    });

    socket.on('voting_cleared', (data) => {
      onEvent?.('voting_cleared', data);
    });

    // Timer events
    socket.on('timer_started', (data) => {
      onEvent?.('timer_started', data);
    });

    socket.on('timer_sync', (data) => {
      onEvent?.('timer_sync', data);
    });

    socket.on('timer_paused', (data) => {
      onEvent?.('timer_paused', data);
    });

    socket.on('timer_reset', (data) => {
      onEvent?.('timer_reset', data);
    });

    // Cleanup on unmount
    return () => {
      socket.emit('leave_space', { space_id: spaceId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [spaceId, user, onEvent]);

  // Emit cursor position
  const emitCursorMove = useCallback((x, y) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('cursor_move', {
        space_id: spaceId,
        x,
        y,
      });
    }
  }, [spaceId]);

  // Emit element creation
  const emitElementCreate = useCallback((element) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('element_create', {
        space_id: spaceId,
        element,
      });
    }
  }, [spaceId]);

  // Emit element update
  const emitElementUpdate = useCallback((element) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('element_update', {
        space_id: spaceId,
        element,
      });
    }
  }, [spaceId]);

  // Emit element delete
  const emitElementDelete = useCallback((elementId) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('element_delete', {
        space_id: spaceId,
        element_id: elementId,
      });
    }
  }, [spaceId]);

  // Emit reaction add
  const emitReactionAdd = useCallback((elementId, emoji, userId) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('reaction_add', {
        space_id: spaceId,
        element_id: elementId,
        emoji,
        user_id: userId,
      });
    }
  }, [spaceId]);

  // Emit reaction remove
  const emitReactionRemove = useCallback((elementId, emoji, userId) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('reaction_remove', {
        space_id: spaceId,
        element_id: elementId,
        emoji,
        user_id: userId,
      });
    }
  }, [spaceId]);

  // Emit voting start
  const emitVotingStart = useCallback((votesPerPerson, anonymous = false) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('voting_start', {
        space_id: spaceId,
        votes_per_person: votesPerPerson,
        anonymous: anonymous,
      });
    }
  }, [spaceId]);

  // Emit voting end
  const emitVotingEnd = useCallback(() => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('voting_end', {
        space_id: spaceId,
      });
    }
  }, [spaceId]);

  // Emit vote clear (reset all votes)
  const emitVotingClear = useCallback(() => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('voting_clear', {
        space_id: spaceId,
      });
    }
  }, [spaceId]);

  // Emit vote cast
  const emitVoteCast = useCallback((elementId, userId, voteCount) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('vote_cast', {
        space_id: spaceId,
        element_id: elementId,
        user_id: userId,
        vote_count: voteCount,
      });
    }
  }, [spaceId]);

  // Timer emit functions
  const emitTimerStart = useCallback((timeSeconds, startedBy) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('timer_start', {
        space_id: spaceId,
        time_seconds: timeSeconds,
        started_by: startedBy,
      });
    }
  }, [spaceId]);

  const emitTimerSync = useCallback((timeLeft, isRunning) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('timer_sync', {
        space_id: spaceId,
        time_left: timeLeft,
        is_running: isRunning,
      });
    }
  }, [spaceId]);

  const emitTimerPause = useCallback((timeLeft) => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('timer_pause', {
        space_id: spaceId,
        time_left: timeLeft,
      });
    }
  }, [spaceId]);

  const emitTimerReset = useCallback(() => {
    if (socketRef.current?.connected && spaceId) {
      socketRef.current.emit('timer_reset', {
        space_id: spaceId,
      });
    }
  }, [spaceId]);

  return {
    connected,
    activeUsers,
    emitCursorMove,
    emitElementCreate,
    emitElementUpdate,
    emitElementDelete,
    emitReactionAdd,
    emitReactionRemove,
    emitVotingStart,
    emitVotingEnd,
    emitVotingClear,
    emitVoteCast,
    emitTimerStart,
    emitTimerSync,
    emitTimerPause,
    emitTimerReset,
  };
}
